import { FaQuestionCircle, FaCog, FaUser, FaFileInvoice, FaMoneyBillWave, FaFolder, FaCheckCircle, FaLock, FaReceipt, FaEnvelope, FaPhone, FaWhatsapp, FaComments, FaFileAlt, FaClipboardCheck, FaFileUpload } from 'react-icons/fa';
import { useTheme } from '../../context/SocketContext';

const HelpGuide = () => {
  const { currentThemeColors } = useTheme();
  const steps = [
    { step: 1, title: 'Initial Information Required', status: 'in-progress', color: 'from-dark-700 to-dark-800' },
    { step: 2, title: 'Tax Return Preparation', status: 'pending', color: 'from-primary-600 to-primary-700' },
    { step: 3, title: 'Draft Tax Return', status: 'pending', color: 'from-primary-600 to-primary-700' },
    { step: 4, title: 'Tax Filing', status: 'pending', color: 'from-primary-600 to-primary-700' },
  ];

  const services = [
    { name: 'Tax Filing', status: 'Active', icon: FaCheckCircle, active: true },
    { name: 'Bookkeeping', status: 'Upgrade available', icon: FaLock, active: false },
    { name: 'USA Company Setup', status: 'Optional', icon: FaLock, active: false },
    { name: 'CFO Advisory', status: 'On demand', icon: FaLock, active: false },
  ];

  const accountSettings = [
    { title: 'My Profile', description: 'View and update your personal details', icon: FaUser },
    { title: 'Invoice Download', description: 'Access invoices for all online payments made through the portal', icon: FaReceipt },
    { title: 'Payment History', description: 'View the complete history of your transactions through website portal', icon: FaMoneyBillWave },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="card p-6 md:p-8 opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}>
        <h1 className="text-2xl md:text-3xl font-bold text-dark-800 dark:text-white mb-3">
          Welcome to the <span style={{ color: 'var(--primary-500)' }}>BooksComply</span> Help Portal!
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          This guide walks you through the key features and functionalities available to help you navigate and make the most of our platform.
        </p>
      </div>

      {/* Help Guide Section */}
      <div className="card p-6 opacity-0 animate-fade-in-up hover:shadow-xl transition-shadow duration-300" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="flex items-center gap-3 mb-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-110 hover:rotate-6"
            style={{ background: `linear-gradient(135deg, var(--primary-500), var(--primary-600))`, boxShadow: `0 10px 15px -3px var(--glow-color)` }}
          >
            <FaQuestionCircle className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-xl font-bold text-dark-800 dark:text-white">Help Guide</h2>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Explore how the portal works in detail through our Help Guide. Find answers, instructions, and tips to use every section efficiently.
        </p>
      </div>

      {/* Account Settings Section */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
        <div className="flex items-center gap-3 mb-6">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-transform duration-300 hover:scale-110 hover:rotate-6"
            style={{ background: `linear-gradient(135deg, var(--accent-500), var(--accent-600))` }}
          >
            <FaCog className="w-6 h-6 text-white animate-spin-slow" />
          </div>
          <h2 className="text-xl font-bold text-dark-800 dark:text-white">Account Settings</h2>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          The Account Settings section is divided into three key parts:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {accountSettings.map((item, index) => (
            <div 
              key={index} 
              className="p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group cursor-pointer"
              style={{ borderColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--primary-300)'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
            >
              <div className="flex items-center gap-3 mb-2">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                  style={{ background: 'var(--primary-100)' }}
                >
                  <item.icon className="w-5 h-5" style={{ color: 'var(--primary-600)' }} />
                </div>
                <h3 className="font-semibold text-dark-800 dark:text-white">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Documents Section */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg shadow-green-500/30 transition-transform duration-300 hover:scale-110 hover:rotate-6">
            <FaFolder className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-xl font-bold text-dark-800 dark:text-white">Documents</h2>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Your documents are organized into two categories:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-5 rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800/30">
            <h3 className="font-bold text-dark-800 dark:text-white mb-2">1. Main Documents</h3>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-primary-500 mt-1">•</span>
                <span>Contains files uploaded during Step 1: Initial Information Required.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary-500 mt-1">•</span>
                <span>Easily accessible for your review at any time.</span>
              </li>
            </ul>
          </div>
          <div className="p-5 rounded-xl bg-secondary-50 dark:bg-secondary-900/20 border border-secondary-100 dark:border-secondary-800/30">
            <h3 className="font-bold text-dark-800 dark:text-white mb-2">2. Chat Documents</h3>
            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-secondary-500 mt-1">•</span>
                <span>Includes files shared during your conversations with our team through the chat feature.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-secondary-500 mt-1">•</span>
                <span>All files exchanged in chats are stored here.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Pricing Plan Section */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
        <h2 className="text-xl font-bold text-dark-800 dark:text-white mb-6">Pricing Plan: Services You Can Use</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((service, index) => (
            <div 
              key={index} 
              className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                service.active 
                  ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20' 
                  : 'border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <service.icon className={`w-5 h-5 ${service.active ? 'text-green-500' : 'text-gray-400 dark:text-gray-500'}`} />
                <div>
                  <p className={`font-semibold ${service.active ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    {service.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{service.status}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tax Filing Steps Section */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
        <h2 className="text-xl font-bold text-dark-800 dark:text-white mb-3">Tax Filing Portal: Track Your Tax Return</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">Stay updated with your return's progress through these steps:</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((step, index) => (
            <div 
              key={index}
              className={`p-5 rounded-xl bg-gradient-to-br ${step.color} text-white transition-all duration-300 hover:scale-[1.05] hover:shadow-2xl hover:-translate-y-2 cursor-pointer group`}
              style={{ animationDelay: `${600 + index * 100}ms` }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold">Step {step.step}</span>
                <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${
                  step.status === 'in-progress' ? 'bg-white/20' : 'bg-white/10'
                }`}>
                  {step.status === 'in-progress' ? 'In Progress' : 'Pending'}
                </span>
              </div>
              <h3 className="font-medium text-lg">{step.title}</h3>
              {step.status === 'in-progress' && (
                <div className="mt-4">
                  <div className="h-1.5 bg-white/30 rounded-full overflow-hidden">
                    <div className="h-full w-0 bg-white rounded-full" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Support Section */}
      <div className="card p-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '700ms', animationFillMode: 'forwards' }}>
        <h2 className="text-xl font-bold text-dark-800 dark:text-white mb-4">Support/Questions/Connect with us:</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Click the <strong className="text-primary-600 dark:text-primary-400">Chat with us!</strong> button (top-right) for support.
        </p>
        
        <h3 className="font-bold text-dark-800 dark:text-white mb-4">Need Help?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <a 
            href="mailto:contact@bookscomply.com"
            className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 hover:border-primary-300 dark:hover:border-primary-600 transition-all duration-200"
          >
            <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <FaEnvelope className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="font-semibold text-dark-800 dark:text-white">Email</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">contact@bookscomply.com</p>
            </div>
          </a>
          <a 
            href="https://wa.me/919428482371"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 hover:border-green-300 dark:hover:border-green-600 transition-all duration-200"
          >
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <FaWhatsapp className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="font-semibold text-dark-800 dark:text-white">WhatsApp</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">+91-9428482371</p>
            </div>
          </a>
        </div>
        
        <div className="p-4 rounded-xl bg-secondary-50 dark:bg-secondary-900/20 border border-secondary-100 dark:border-secondary-800/30">
          <p className="text-sm text-secondary-700 dark:text-secondary-300">
            🔔 Notifications will alert you when a message/task needs your attention.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpGuide;